import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);

const STORAGE_KEY = "apwo_cart_v1";

const loadInitial = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (_) {
    return {};
  }
};

export const CartProvider = ({ children }) => {
  // Cart shape: { [product_id]: qty }. Only SKU IDs + integer quantities. Never prices.
  const [cart, setCart] = useState(loadInitial);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(cart)); } catch (_) {}
  }, [cart]);

  const setQty = useCallback((product_id, qty) => {
    setCart((c) => {
      const next = { ...c };
      const n = Math.max(0, Math.min(10000, parseInt(qty || 0, 10) || 0));
      if (n === 0) delete next[product_id];
      else next[product_id] = n;
      return next;
    });
  }, []);

  const inc = useCallback((product_id, delta = 1) => {
    setCart((c) => {
      const next = { ...c };
      const cur = next[product_id] || 0;
      const n = Math.max(0, Math.min(10000, cur + delta));
      if (n === 0) delete next[product_id]; else next[product_id] = n;
      return next;
    });
  }, []);

  const clear = useCallback(() => setCart({}), []);

  const asLines = useCallback(() => Object.entries(cart)
    .filter(([, q]) => q > 0)
    .map(([product_id, qty]) => ({ product_id, qty })), [cart]);

  const totalUnits = useMemo(
    () => Object.values(cart).reduce((s, q) => s + q, 0), [cart]);

  const value = useMemo(() => ({
    cart, setQty, inc, clear, asLines, totalUnits,
  }), [cart, setQty, inc, clear, asLines, totalUnits]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside <CartProvider>");
  return ctx;
};
